import type { File as EvidenceFile } from 'src/components/moleculesLegacy/FileUpload';
import type { DynamicQuestionAnswerValue } from 'src/components/molecules';
import { FILEPOND_PROCESSING_COMPLETE } from 'src/components/molecules/DynamicQuestion/DynamicQuestionProps';
import { UploadScanStatus } from 'src/types';
import {
  buildEvidenceUploadFilesFromPersistedUploads,
  evidenceS3SourceFromQaFileKey,
} from 'src/utils/dynamicQuestionUploadedFiles';
import { guessPrimaryMimeTypeForAllowedFilename } from 'src/utils/validateFileUpload/fileValidation';

type QuestionForHydration = {
  id?: string | null;
  questionText?: string | null;
  questionType?: { typeKey?: string | null } | null;
};

const FILE_UPLOAD_TYPE_KEY = 'file_upload';

export type HydrateFileUploadEvidenceOptions = {
  evidenceReportId?: string | null;
};

function evidenceFilesFromQaAttachments(
  attachments: NonNullable<DynamicQuestionAnswerValue['qaAnswerAttachments']>,
  reportId: string,
): EvidenceFile[] {
  const timestamp = new Date().toISOString();
  return attachments.flatMap((att) => {
    const source = evidenceS3SourceFromQaFileKey(att.fileKey, reportId);
    if (source === '') {
      return [];
    }
    const name = att.fileName ?? 'attachment';
    return [
      {
        source,
        options: {
          type: 'local' as const,
          file: {
            name,
            size: att.fileSize ?? 0,
            type: att.mimeType ?? guessPrimaryMimeTypeForAllowedFilename(name),
          },
          metadata: {
            scanStatus: UploadScanStatus.PENDING,
            ...(att.id != null && att.id !== ''
              ? { qaAttachmentId: att.id }
              : {}),
          },
        },
        status: FILEPOND_PROCESSING_COMPLETE,
        created: timestamp,
        updated: timestamp,
      },
    ];
  });
}

export function hydrateFileUploadEvidenceInAnswersState(
  answers: Record<string, DynamicQuestionAnswerValue>,
  questions: QuestionForHydration[],
  options?: HydrateFileUploadEvidenceOptions,
): Record<string, DynamicQuestionAnswerValue> {
  const evidenceReportId = options?.evidenceReportId ?? null;
  let changed = false;
  const next = { ...answers };
  for (const question of questions) {
    if (question?.questionType?.typeKey !== FILE_UPLOAD_TYPE_KEY) continue;
    const questionId = question?.id != null ? String(question.id) : '';
    if (!questionId) continue;
    const questionText = question?.questionText?.trim() ?? '';
    const value =
      next[questionId] ??
      (questionText ? next[`questionText:${questionText}`] : undefined);
    if (value == null || typeof value !== 'object') continue;
    const uploaded = value.uploadedFiles ?? [];
    const evidence = value.evidenceUploadFiles ?? [];
    const qaAttachments = value.qaAnswerAttachments ?? [];
    if (evidenceReportId && qaAttachments.length > 0 && evidence.length === 0) {
      const evidenceUploadFiles = evidenceFilesFromQaAttachments(
        qaAttachments,
        evidenceReportId,
      );
      if (evidenceUploadFiles.length > 0) {
        const patch: DynamicQuestionAnswerValue = {
          ...value,
          evidenceUploadFiles,
          fileNames: evidenceUploadFiles.map((f) => f.options.file.name),
          uploadedFiles: undefined,
        };
        next[questionId] = patch;
        if (questionText) {
          next[`questionText:${questionText}`] = patch;
        }
        changed = true;
        continue;
      }
    }
    if (uploaded.length === 0 || evidence.length > 0) continue;
    const { valid: evidenceUploadFiles } =
      buildEvidenceUploadFilesFromPersistedUploads(uploaded);
    const patch: DynamicQuestionAnswerValue = {
      ...value,
      evidenceUploadFiles,
      fileNames: evidenceUploadFiles.map((f) => f.options.file.name),
      uploadedFiles: undefined,
    };
    next[questionId] = patch;
    if (questionText) {
      next[`questionText:${questionText}`] = patch;
    }
    changed = true;
  }
  return changed ? next : answers;
}
